import type { AuthorType, Direction } from '@support/shared';

import type { ContactSummaryDto } from './types';

/**
 * Форматирование и разбор текста — ровно то же, что в старой панели.
 *
 * Файл перенесён вместе с дизайном; поменялись только формы данных (новое API
 * отдаёт camelCase и enum'ы в верхнем регистре). Логика группировки сообщений,
 * подсветки ссылок и разбора markdown оставлена без изменений: операторы уже
 * привыкли к тому, как это выглядит.
 */

// ──────────────────────────── Аватары и имена ────────────────────────────

const GRADIENTS: readonly (readonly [string, string])[] = [
  ['#fb7185', '#f43f5e'],
  ['#fb923c', '#ea580c'],
  ['#fbbf24', '#d97706'],
  ['#34d399', '#059669'],
  ['#22d3ee', '#0891b2'],
  ['#60a5fa', '#2563eb'],
  ['#818cf8', '#4f46e5'],
  ['#c084fc', '#7c3aed'],
  ['#f472b6', '#db2777'],
  ['#facc15', '#ca8a04'],
];

function hash(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}

/** Цвет-заглушка вместо аватара. Стабилен по id: контакт не меняет цвет. */
export function avatarGradient(id: string): string {
  const pair = GRADIENTS[hash(id) % GRADIENTS.length] ?? GRADIENTS[0];
  return `linear-gradient(135deg, ${pair?.[0] ?? '#818cf8'}, ${pair?.[1] ?? '#4f46e5'})`;
}

/** Минимум, которого хватает для подписи: строку списка рисуют и по нему. */
export interface NamedContact {
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  displayName?: string | null;
  tgUserId?: string | null;
}

/**
 * Подпись контакта. Готовый displayName приходит с сервера — считаем сами
 * только там, где его нет (оптимистичная отрисовка, черновики).
 */
export function contactTitle(contact: NamedContact): string {
  if (contact.displayName) return contact.displayName;
  const full = [contact.firstName, contact.lastName]
    .filter((part): part is string => typeof part === 'string' && part.trim().length > 0)
    .join(' ')
    .trim();
  if (full) return full;
  if (contact.username) return `@${contact.username}`;
  return contact.tgUserId ?? '—';
}

export function initials(contact: NamedContact | ContactSummaryDto): string {
  const title = contactTitle(contact).replace(/^@/, '');
  const parts = title.split(/\s+/).filter(Boolean);
  const first = parts[0];
  const second = parts[1];
  if (first && second) return (first.slice(0, 1) + second.slice(0, 1)).toUpperCase();
  return (title.slice(0, 2) || '?').toUpperCase();
}

/** Инициалы оператора: у него одно поле name, а не имя с фамилией врозь. */
export function userInitials(name: string | null | undefined): string {
  const parts = (name ?? '').split(/\s+/).filter(Boolean);
  const first = parts[0];
  const second = parts[1];
  if (first && second) return (first.slice(0, 1) + second.slice(0, 1)).toUpperCase();
  return ((first ?? '?').slice(0, 2) || '?').toUpperCase();
}

// ──────────────────────────── Время ────────────────────────────

export function fmtTime(iso: string | null | undefined): string {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

/** Время в строке списка: сегодня — часы, на неделе — день, дальше — дата. */
export function fmtListTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return fmtTime(iso);
  const days = (now.getTime() - date.getTime()) / 86_400_000;
  if (days < 7) return date.toLocaleDateString('ru-RU', { weekday: 'short' });
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' });
}

/** Подпись разделителя дня в ленте переписки. */
export function fmtDay(iso: string): string {
  const date = new Date(iso);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === today.toDateString()) return 'Сегодня';
  if (date.toDateString() === yesterday.toDateString()) return 'Вчера';
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  });
}

export function fmtSize(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1_048_576) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1_048_576).toFixed(1)} МБ`;
}

/** Длительность голосового: 1:07. */
export function fmtDuration(sec: number | null | undefined): string {
  if (!sec) return '';
  const minutes = Math.floor(sec / 60);
  const seconds = sec % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/** Ожидание и время ответа: «23 мин», «1 ч 12 мин». */
export function fmtDurationLong(sec: number | null | undefined): string {
  if (sec == null || Number.isNaN(sec)) return '—';
  if (sec < 60) return `${Math.round(sec)} с`;
  const minutes = Math.floor(sec / 60);
  const seconds = Math.round(sec - minutes * 60);
  if (minutes < 60) return seconds ? `${minutes} м ${seconds} с` : `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  return `${hours} ч ${minutes - hours * 60} мин`;
}

// ──────────────────────────── Группировка сообщений ────────────────────────────

/** Положение сообщения внутри непрерывной группы одного автора. */
export type GroupPos = 'single' | 'first' | 'middle' | 'last';

/** Пауза, после которой соседние сообщения перестают быть одной группой. */
const GROUP_GAP_MS = 5 * 60 * 1000;

/** Минимум полей, по которым решается группировка. */
export interface GroupableMessage {
  direction: Direction;
  authorType: AuthorType;
  createdAt: string;
}

function sameGroup(a: GroupableMessage, b: GroupableMessage): boolean {
  return (
    a.authorType === b.authorType &&
    a.direction === b.direction &&
    // Системная плашка не склеивается ни с чем: она и выглядит иначе.
    a.authorType !== 'SYSTEM' &&
    Math.abs(new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) < GROUP_GAP_MS
  );
}

export function groupPos(messages: readonly GroupableMessage[], index: number): GroupPos {
  const current = messages[index];
  if (!current || current.authorType === 'SYSTEM') return 'single';

  const prev = messages[index - 1];
  const next = messages[index + 1];
  const prevSame = !!prev && sameGroup(current, prev);
  const nextSame = !!next && sameGroup(current, next);

  if (prevSame && nextSame) return 'middle';
  if (prevSame) return 'last';
  if (nextSame) return 'first';
  return 'single';
}

// ──────────────────────────── Ссылки и markdown ────────────────────────────

const URL_RE = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/g;

export type LinkPart = string | { url: string; label: string };

/** Разбить текст на куски со ссылками — для сообщений без разметки. */
export function linkify(text: string): LinkPart[] {
  const out: LinkPart[] = [];
  let last = 0;
  for (const match of text.matchAll(URL_RE)) {
    const at = match.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    const raw = match[0];
    out.push({ url: raw.startsWith('http') ? raw : `https://${raw}`, label: raw });
    last = at + raw.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export type MdNode =
  | { kind: 'text'; value: string }
  | { kind: 'br' }
  | { kind: 'bold'; children: MdNode[] }
  | { kind: 'italic'; children: MdNode[] }
  | { kind: 'strike'; children: MdNode[] }
  | { kind: 'code'; value: string }
  | { kind: 'pre'; value: string }
  | { kind: 'link'; url: string; label: string };

const INLINE_RE =
  /(\*\*(.+?)\*\*)|(__(.+?)__)|(~~(.+?)~~)|(`([^`]+)`)|(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\))/g;

function parseInline(text: string): MdNode[] {
  const out: MdNode[] = [];
  let last = 0;
  for (const match of text.matchAll(INLINE_RE)) {
    const at = match.index ?? 0;
    if (at > last) out.push(...autoLinkText(text.slice(last, at)));

    if (match[1]) out.push({ kind: 'bold', children: parseInline(match[2] ?? '') });
    else if (match[3]) out.push({ kind: 'italic', children: parseInline(match[4] ?? '') });
    else if (match[5]) out.push({ kind: 'strike', children: parseInline(match[6] ?? '') });
    else if (match[7]) out.push({ kind: 'code', value: match[8] ?? '' });
    else if (match[9]) out.push({ kind: 'link', url: match[11] ?? '', label: match[10] ?? '' });

    last = at + match[0].length;
  }
  if (last < text.length) out.push(...autoLinkText(text.slice(last)));
  return out;
}

function autoLinkText(text: string): MdNode[] {
  const out: MdNode[] = [];
  let last = 0;
  for (const match of text.matchAll(URL_RE)) {
    const at = match.index ?? 0;
    if (at > last) out.push({ kind: 'text', value: text.slice(last, at) });
    const raw = match[0];
    out.push({ kind: 'link', url: raw.startsWith('http') ? raw : `https://${raw}`, label: raw });
    last = at + raw.length;
  }
  if (last < text.length) out.push({ kind: 'text', value: text.slice(last) });
  return out;
}

/**
 * Блок кода вместе с необязательным токеном языка после открывающих кавычек.
 *
 * Язык вынесен в отдельную группу и без пробелов внутри намеренно: срезать
 * первую строку безусловно нельзя — у блока вида ```const a = 1\nconst b = 2```
 * первая строка это код, и она бы исчезла.
 */
const CODE_BLOCK_RE = /```([A-Za-z0-9+#-]*)\n?([\s\S]*?)```/g;

/**
 * Разбор подмножества markdown, которое понимает Telegram.
 *
 * Свой парсер, а не библиотека: рисуем ровно то, что доедет до клиента в
 * Telegram, и ни одним тегом больше. Полноценный markdown показал бы оператору
 * заголовки и таблицы, которых в сообщении не будет.
 */
export function parseMarkdown(text: string): MdNode[] {
  if (!text) return [];
  const out: MdNode[] = [];
  let last = 0;

  for (const match of text.matchAll(CODE_BLOCK_RE)) {
    const at = match.index ?? 0;
    if (at > last) pushLines(out, text.slice(last, at));
    // Завершающий перевод строки убираем: он появился от закрывающих кавычек
    // на своей строке, а в <pre> дал бы лишнюю пустую строку внизу блока.
    out.push({ kind: 'pre', value: (match[2] ?? '').replace(/\n$/, '') });
    last = at + match[0].length;
  }
  pushLines(out, text.slice(last));
  return out;
}

function pushLines(out: MdNode[], chunk: string): void {
  const lines = chunk.split('\n');
  lines.forEach((line, index) => {
    out.push(...parseInline(line));
    if (index < lines.length - 1) out.push({ kind: 'br' });
  });
}

// ──────────────────────────── Подписи статусов ────────────────────────────

/** Статусы диалога по-русски. Ключи — ConversationStatus из @support/shared. */
export const STATUS_LABEL: Record<string, string> = {
  NEW: 'Новый',
  OPEN: 'В работе',
  PENDING: 'Ожидает',
  QUALIFIED: 'Квалифицирован',
  CLOSED: 'Закрыт',
  BLOCKED: 'Заблокирован',
};

/** Роли по-русски. Ключи — Role из @support/shared. */
export const ROLE_LABEL: Record<string, string> = {
  OWNER: 'Владелец',
  ADMIN: 'Администратор',
  SUPERVISOR: 'Супервизор',
  AGENT: 'Оператор',
  VIEWER: 'Наблюдатель',
};
